/**
 * Pagination state management hook for DataList.
 *
 * Manages current page, page size, total pages computation,
 * and load-more behaviour for scroll mode.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';

import type { PaginationConfig } from '../types';
import { DEFAULT_PAGE_SIZE } from '../constants';

// ---------------------------------------------------------------------------
// Public return type
// ---------------------------------------------------------------------------

export interface UseListPaginationReturn {
  /** Current page (1-indexed). */
  page: number;

  /** Current page size. */
  pageSize: number;

  /** Total number of pages. */
  totalPages: number;

  /** Navigate to a specific page (clamped to valid range). */
  setPage: (page: number) => void;

  /** Change page size and reset to page 1. */
  setPageSize: (size: number) => void;

  /** Increment page by 1 (for scroll mode). No-op when no more items. */
  loadMore: () => void;

  /** Whether there are more items beyond the current page. */
  hasMore: boolean;
}

// ---------------------------------------------------------------------------
// In-memory page store – survives component remounts within the same SPA
// session but resets on full page reload (e.g. login redirect).
// ---------------------------------------------------------------------------

const pageStore = new Map<string, number>();

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useListPagination(
  config: PaginationConfig | undefined,
  totalItems: number,
  listId?: string,
): UseListPaginationReturn {
  const initialPageSize = config?.pageSize ?? DEFAULT_PAGE_SIZE;
  const persistPage = config?.persistPage === true;
  const storeKey = persistPage && listId ? listId : null;

  const [currentPage, setCurrentPage] = useState(() => {
    if (storeKey) {
      return pageStore.get(storeKey) ?? 1;
    }
    return 1;
  });
  const [currentPageSize, setCurrentPageSize] = useState(initialPageSize);

  useEffect(() => {
    if (storeKey) {
      pageStore.set(storeKey, currentPage);
    }
  }, [currentPage, storeKey]);

  // ---- Derived values ----

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / currentPageSize)),
    [totalItems, currentPageSize],
  );

  const hasMore = useMemo(
    () => currentPage * currentPageSize < totalItems,
    [currentPage, currentPageSize, totalItems],
  );

  // ---- Actions ----

  const setPage = useCallback(
    (target: number) => {
      setCurrentPage((prev) => {
        const maxPage = Math.max(1, Math.ceil(totalItems / currentPageSize));
        const clamped = Math.max(1, Math.min(target, maxPage));
        return clamped === prev ? prev : clamped;
      });
    },
    [totalItems, currentPageSize],
  );

  const setPageSize = useCallback((size: number) => {
    setCurrentPageSize(size);
    setCurrentPage(1);
  }, []);

  const loadMore = useCallback(() => {
    setCurrentPage((prev) => {
      const maxPage = Math.max(1, Math.ceil(totalItems / currentPageSize));
      return prev < maxPage ? prev + 1 : prev;
    });
  }, [totalItems, currentPageSize]);

  // ---- Return ----

  return useMemo(
    () => ({
      page: currentPage,
      pageSize: currentPageSize,
      totalPages,
      setPage,
      setPageSize,
      loadMore,
      hasMore,
    }),
    [currentPage, currentPageSize, totalPages, setPage, setPageSize, loadMore, hasMore],
  );
}
